import { useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { listLeads, updateLeadStatus } from "@/lib/admin-leads.functions";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

export const Route = createFileRoute("/_authenticated/admin/leads")({
  head: () => ({
    meta: [
      { title: "Leads — Admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: LeadsPage,
});

type Lead = {
  id: string;
  created_at: string;
  updated_at: string | null;
  full_name: string;
  business_name: string;
  email: string;
  phone: string;
  city: string;
  state: string;
  street: string;
  zip: string;
  employees: number | null;
  daily_visitors: number | null;
  service_interest: string | null;
  machine_type: string | null;
  office_type: string | null;
  shift_pattern: string | null;
  total_building_headcount: number | null;
  business_hours: string | null;
  access_level: string | null;
  location_notes: string | null;
  follow_up_status: string;
  follow_up_notes: string | null;
};

const STATUSES = ["new", "contacted", "qualified", "won", "lost"] as const;
type Status = (typeof STATUSES)[number];

const STATUS_COLOR: Record<string, string> = {
  new: "bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/30",
  contacted: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30",
  qualified: "bg-purple-500/15 text-purple-700 dark:text-purple-300 border-purple-500/30",
  won: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30",
  lost: "bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/30",
};

function csvEscape(v: unknown): string {
  if (v == null) return "";
  const s = String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function downloadCsv(leads: Lead[]) {
  const cols: (keyof Lead)[] = [
    "created_at",
    "follow_up_status",
    "full_name",
    "business_name",
    "email",
    "phone",
    "street",
    "city",
    "state",
    "zip",
    "employees",
    "daily_visitors",
    "total_building_headcount",
    "service_interest",
    "machine_type",
    "office_type",
    "shift_pattern",
    "business_hours",
    "access_level",
    "location_notes",
    "follow_up_notes",
    "id",
  ];
  const header = cols.join(",");
  const rows = leads.map((l) => cols.map((c) => csvEscape(l[c])).join(","));
  const csv = [header, ...rows].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `leads-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function LeadsPage() {
  const fetchLeads = useServerFn(listLeads);
  const updateStatus = useServerFn(updateLeadStatus);
  const qc = useQueryClient();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["admin", "leads"],
    queryFn: () => fetchLeads(),
    staleTime: 30_000,
  });

  const [statusFilter, setStatusFilter] = useState<Status | "all">("all");
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (payload: { id: string; follow_up_status: Status; follow_up_notes?: string }) =>
      updateStatus({ data: payload }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "leads"] }),
  });

  const leads: Lead[] = data?.leads ?? [];

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return leads.filter((l) => {
      if (statusFilter !== "all" && l.follow_up_status !== statusFilter) return false;
      if (!term) return true;
      return [l.full_name, l.business_name, l.email, l.phone, l.city, l.state]
        .filter(Boolean)
        .some((v) => v!.toLowerCase().includes(term));
    });
  }, [leads, statusFilter, q]);

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: leads.length };
    for (const s of STATUSES) c[s] = 0;
    for (const l of leads) c[l.follow_up_status] = (c[l.follow_up_status] ?? 0) + 1;
    return c;
  }, [leads]);

  async function signOut() {
    await supabase.auth.signOut();
    window.location.href = "/auth";
  }

  return (
    <main className="mx-auto max-w-7xl px-4 sm:px-6 py-8">
      <header className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold">Leads Dashboard</h1>
          <p className="text-sm text-muted-foreground">
            {leads.length} total • showing {filtered.length}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => refetch()}>Refresh</Button>
          <Button onClick={() => downloadCsv(filtered)}>Export CSV</Button>
          <Button variant="ghost" onClick={signOut}>Sign out</Button>
        </div>
      </header>

      <div className="flex flex-wrap gap-2 mb-4">
        {(["all", ...STATUSES] as const).map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 rounded-full text-sm border transition ${
              statusFilter === s
                ? "bg-foreground text-background border-foreground"
                : "bg-background hover:bg-muted"
            }`}
          >
            {s} <span className="opacity-60">({counts[s] ?? 0})</span>
          </button>
        ))}
        <Input
          placeholder="Search name, business, email, city…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="ml-auto max-w-sm"
        />
      </div>

      {isLoading && <p className="text-sm text-muted-foreground">Loading leads…</p>}
      {error && (
        <p className="text-sm text-destructive">
          Failed to load: {(error as Error).message}
        </p>
      )}

      <div className="rounded-md border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left">
            <tr>
              <th className="p-3 font-medium">Date</th>
              <th className="p-3 font-medium">Status</th>
              <th className="p-3 font-medium">Contact</th>
              <th className="p-3 font-medium">Business</th>
              <th className="p-3 font-medium">Location</th>
              <th className="p-3 font-medium">Interest</th>
              <th className="p-3 font-medium">Employees</th>
              <th className="p-3 font-medium"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((l) => {
              const isOpen = expanded === l.id;
              return (
                <>
                  <tr key={l.id} className="border-t hover:bg-muted/30">
                    <td className="p-3 whitespace-nowrap">
                      {new Date(l.created_at).toLocaleDateString()}
                    </td>
                    <td className="p-3">
                      <Badge
                        variant="outline"
                        className={STATUS_COLOR[l.follow_up_status] ?? ""}
                      >
                        {l.follow_up_status}
                      </Badge>
                    </td>
                    <td className="p-3">
                      <div className="font-medium">{l.full_name}</div>
                      <div className="text-xs text-muted-foreground">
                        <a href={`mailto:${l.email}`} className="underline">{l.email}</a>
                        {" · "}
                        <a href={`tel:${l.phone}`} className="underline">{l.phone}</a>
                      </div>
                    </td>
                    <td className="p-3">{l.business_name}</td>
                    <td className="p-3 whitespace-nowrap">
                      {l.city}, {l.state}
                    </td>
                    <td className="p-3">{l.service_interest || l.machine_type || "—"}</td>
                    <td className="p-3">{l.employees ?? "—"}</td>
                    <td className="p-3 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setExpanded(isOpen ? null : l.id)}
                      >
                        {isOpen ? "Hide" : "Details"}
                      </Button>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="bg-muted/20 border-t">
                      <td colSpan={8} className="p-4">
                        <div className="grid gap-4 md:grid-cols-2">
                          <div>
                            <h3 className="text-sm font-semibold mb-2">Details</h3>
                            <dl className="text-sm grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
                              <dt className="text-muted-foreground">Address</dt>
                              <dd>{l.street}, {l.city} {l.zip}</dd>
                              <dt className="text-muted-foreground">Office type</dt>
                              <dd>{l.office_type ?? "—"}</dd>
                              <dt className="text-muted-foreground">Shift pattern</dt>
                              <dd>{l.shift_pattern ?? "—"}</dd>
                              <dt className="text-muted-foreground">Bldg headcount</dt>
                              <dd>{l.total_building_headcount ?? "—"}</dd>
                              <dt className="text-muted-foreground">Daily visitors</dt>
                              <dd>{l.daily_visitors ?? "—"}</dd>
                              <dt className="text-muted-foreground">Business hours</dt>
                              <dd>{l.business_hours ?? "—"}</dd>
                              <dt className="text-muted-foreground">Access</dt>
                              <dd>{l.access_level ?? "—"}</dd>
                              <dt className="text-muted-foreground">Machine type</dt>
                              <dd>{l.machine_type ?? "—"}</dd>
                              <dt className="text-muted-foreground">Notes</dt>
                              <dd className="whitespace-pre-wrap">{l.location_notes ?? "—"}</dd>
                            </dl>
                          </div>
                          <div>
                            <h3 className="text-sm font-semibold mb-2">Follow-up</h3>
                            <StatusEditor
                              lead={l}
                              onSave={(status, notes) =>
                                mutation.mutate({
                                  id: l.id,
                                  follow_up_status: status,
                                  follow_up_notes: notes,
                                })
                              }
                              saving={mutation.isPending}
                            />
                            <div className="mt-6">
                              <h3 className="text-sm font-semibold mb-2">Send to tele-caller</h3>
                              <TeleCallerSend lead={l} />
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              );
            })}
            {!isLoading && filtered.length === 0 && (
              <tr>
                <td colSpan={8} className="p-8 text-center text-muted-foreground">
                  No leads match the current filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </main>
  );
}

function StatusEditor({
  lead,
  onSave,
  saving,
}: {
  lead: Lead;
  onSave: (status: Status, notes: string) => void;
  saving: boolean;
}) {
  const [status, setStatus] = useState<Status>((lead.follow_up_status as Status) || "new");
  const [notes, setNotes] = useState(lead.follow_up_notes ?? "");
  useEffect(() => {
    setStatus((lead.follow_up_status as Status) || "new");
    setNotes(lead.follow_up_notes ?? "");
  }, [lead.id, lead.follow_up_status, lead.follow_up_notes]);
  return (
    <div className="space-y-2">
      <Select value={status} onValueChange={(v) => setStatus(v as Status)}>
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {STATUSES.map((s) => (
            <SelectItem key={s} value={s}>{s}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Textarea
        rows={4}
        placeholder="Follow-up notes"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
      />
      <Button onClick={() => onSave(status, notes)} disabled={saving}>
        {saving ? "Saving…" : "Save"}
      </Button>
    </div>
  );
}

function TeleCallerSend({ lead }: { lead: Lead }) {
  const [to, setTo] = useState("");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState<{ kind: "ok" | "err"; text: string } | null>(null);

  async function send() {
    setMsg(null);
    const trimmed = to.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setMsg({ kind: "err", text: "Enter a valid tele-caller email." });
      return;
    }
    setSending(true);
    try {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess.session?.access_token;
      if (!token) throw new Error("Not signed in");

      const templateData: Record<string, unknown> = {
        full_name: lead.full_name,
        business_name: lead.business_name,
        email: lead.email,
        phone: lead.phone,
        street: lead.street,
        city: lead.city,
        state: lead.state,
        zip: lead.zip,
        employees: lead.employees ?? null,
        daily_visitors: lead.daily_visitors ?? null,
        total_building_headcount: lead.total_building_headcount ?? null,
        business_hours: lead.business_hours ?? null,
        access_level: lead.access_level ?? null,
        machine_type: lead.machine_type ?? null,
        service_interest: lead.service_interest ?? null,
        office_type: lead.office_type ?? null,
        shift_pattern: lead.shift_pattern ?? null,
        location_notes: lead.location_notes ?? null,
        follow_up_status: lead.follow_up_status ?? null,
        follow_up_notes: lead.follow_up_notes ?? null,
        lead_created_at: new Date(lead.created_at).toLocaleString(),
      };
      if (note.trim()) templateData.admin_note = note.trim();

      const res = await fetch("/lovable/email/transactional/send", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          templateName: "lead-forward",
          recipientEmail: trimmed,
          idempotencyKey: `lead-forward-${lead.id}-${trimmed.toLowerCase()}-${Date.now()}`,
          templateData,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json?.success === false) {
        throw new Error(json?.error || json?.reason || `Send failed (${res.status})`);
      }
      setMsg({ kind: "ok", text: `Queued for ${trimmed}.` });
      setNote("");
    } catch (e) {
      setMsg({ kind: "err", text: (e as Error).message });
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="space-y-2">
      <Input
        type="email"
        placeholder="telecaller@example.com"
        value={to}
        onChange={(e) => setTo(e.target.value)}
      />
      <Textarea
        rows={3}
        placeholder="Optional note (e.g. 'Call within 1 hour')"
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />
      <Button onClick={send} disabled={sending}>
        {sending ? "Sending…" : "Email lead to tele-caller"}
      </Button>
      {msg && (
        <p
          className={`text-xs ${
            msg.kind === "ok" ? "text-emerald-600" : "text-destructive"
          }`}
        >
          {msg.text}
        </p>
      )}
    </div>
  );
}

